import { FC } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styles from './OpportunityCard.module.scss';
import appState from '../../../state/AppStateContainer';
import BtButton from '../../../ts-components/bt-button/bt-button';
import TooltipContainer from '../TooltipContainer/TooltipContainer';

interface OpportunityCardProps extends RouteComponentProps {
  title: string;
  description?: string;
  buttonText?: string;
  icon: React.ComponentType;
  buttonIcon?: React.ComponentType;
  redirectTo?: string;
  onButtonClick?: () => void;
  features?: Array<{
    icon?: React.ComponentType;
    title: string;
    description?: string;
    isInfo?: boolean;
  }>;
  price?: string;
  secondaryButtonText?: string;
  notFill?: boolean;
  disabled?: boolean;
  tooltipMessage?: string | React.ReactNode;
  hideButtons?: boolean;
  className?: string;
  fieldClassName?: string;
  style?: React.CSSProperties;
}

const OpportunityCard: FC<OpportunityCardProps> = ({
  title,
  description,
  buttonText,
  icon: Icon,
  buttonIcon: ButtonIcon,
  redirectTo,
  onButtonClick,
  history,
  features = [],
  price,
  secondaryButtonText,
  notFill = false,
  disabled = false,
  tooltipMessage,
  hideButtons = false,
  className,
  fieldClassName,
  style,
}) => {

  const handlePrimaryButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else if (redirectTo) {
      const orgSlug = appState.getCurrentOrgSlug();
      history.push(`/${orgSlug}${redirectTo}`);
    }
  };

  const handleSecondaryButtonClick = () => {
    if (redirectTo) {
      const orgSlug = appState.getCurrentOrgSlug();
      history.push(`/${orgSlug}${redirectTo}`);
    }
  };

  const hasInfoFeatures = features.some(feature => feature.isInfo);
  const hasRegularFeatures = features.some(feature => !feature.isInfo);
  const hasSecondaryButton = !!secondaryButtonText;
  const hasPrimaryButton = !!buttonText;

  return (
    <div 
      className={`${styles.opportunityCard} ${hasInfoFeatures ? styles.hasInfoFeatures : ''} ${hasSecondaryButton ? styles.hasSecondaryButton : ''} ${className || ''} ${fieldClassName || ''}`}
      style={style}
    >      
      <div className={styles.cardHeader}>
        <div className={`${styles.cardIcon} ${notFill ? styles.notFill : ''} cardIcon`}>
          <Icon />
        </div>
        <div className={styles.cardContent}>
          <div className={`${styles.cardTitle} ${!description || description.trim() === '' ? styles.cardTitleNoDescription : ''} cardTitle`}>{title}</div>
          {description && description.trim() !== '' && <div className={`${styles.cardDescription} cardDescription`}>{description}</div>}
        </div>
      </div>

      {features.length > 0 && (
        <div className={`${styles.featuresList} ${hasInfoFeatures ? styles.hasInfoFeatures : ''} ${!hasPrimaryButton && !hasSecondaryButton ? styles.featuresListNoButtons : ''} featuresList`}>
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <div key={index} className={`${styles.featureItem} ${feature.isInfo ? styles.featureItemInfo : ''} featureItem`}>
                {FeatureIcon && (
                  <div className={styles.featureIcon}>
                    <FeatureIcon />
                  </div>
                )}
                <div className={styles.featureContent}>
                  <div className={styles.featureTitle}>{feature.title}</div>
                  <div className={styles.featureDescription}>{feature.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {price && (
        <div className={`${styles.pricing} ${hasRegularFeatures ? styles.pricingWithFeatures : ''} pricing`}>
          <div className={styles.price}>
            {(() => {
              const parts = price.split(' ');
              const hasSlash = price.includes('/');
              
              if (hasSlash) {
                const slashIndex = price.indexOf('/');
                const beforeSlash = price.substring(0, slashIndex).trim();
                const afterSlash = price.substring(slashIndex);
                const beforeParts = beforeSlash.split(' ');
                
                if (beforeParts.length > 2) {
                  const prefix = beforeParts[0];
                  const amount = beforeParts.slice(1).join(' ');
                  return (
                    <>
                      <span className={styles.pricePrefix}>{prefix}</span>
                      <span className={`${styles.priceAmount} priceAmount`}>{amount}</span>
                      <span className={styles.priceUnit}>{afterSlash}</span>
                    </>
                  );
                } else {
                  return (
                    <>
                      <span className={`${styles.priceAmount} priceAmount`}>{beforeSlash}</span>
                      <span className={styles.priceUnit}>{afterSlash}</span>
                    </>
                  );
                }
              } else {
                if (parts.length > 2) {
                  const prefix = parts[0];
                  const amount = parts.slice(1).join(' ');
                  return (
                    <>
                      <span className={styles.pricePrefix}>{prefix}</span>
                      <span className={`${styles.priceAmount} priceAmount`}>{amount}</span>
                    </>
                  );
                } else {
                  return <span className={`${styles.priceAmount} priceAmount`}>{price}</span>;
                }
              }
            })()}
          </div>
        </div>
      )}

      {(hasPrimaryButton || hasSecondaryButton) && (
        <div className={`${styles.buttonContainer} ${hasSecondaryButton ? styles.hasSecondaryButton : ''} ${!hasPrimaryButton ? styles.noPrimaryButton : ''}`}>
          {buttonText && (
            tooltipMessage ? (
              <TooltipContainer 
                renderReferenceComponent={(className: string, ref: React.Ref<HTMLDivElement>) => (
                  <div className={className} ref={ref} style={{ display: 'flex', width: '100%' }}>
                    <BtButton 
                      onClick={handlePrimaryButtonClick}
                      color=""
                      size="base"
                      disabled={disabled}
                      style={{ width: '100%' }}
                    >
                      {ButtonIcon && <ButtonIcon />}
                      {buttonText}
                    </BtButton>
                  </div>
                )}
              >
                {tooltipMessage}
              </TooltipContainer>
            ) : (
              <BtButton 
                onClick={handlePrimaryButtonClick}
                color=""
                size="base"
                disabled={disabled}
              >
                {ButtonIcon && <ButtonIcon />}
                {buttonText}
              </BtButton>
            )
          )}
          {secondaryButtonText && (
            <BtButton 
              onClick={handleSecondaryButtonClick}
              color="white"
              size="base"
            >
              {secondaryButtonText}
            </BtButton>
          )}
        </div>
      )}
    </div>
  );
};

export default withRouter(OpportunityCard);
